import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal } from '@rsos/base-components';
import {
  ModalFooter,
  ModalBody,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { useUpdatePreconfiguredMessageMutation } from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import type { PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import {
  SINGLETON_RESPONSE_TYPES,
  SINGLETON_TYPE_ACTION_LABELS,
  type SingletonResponseType,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesTypes';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { AppState } from '../../../types';
import styles from './DisableAssignedMessageModal.module.css';
import { UPDATE_SUCCESS_MESSAGE } from './utils';

interface Props {
  isOpened: boolean;
  onClose: () => void;
  message: PreconfiguredResponse;
}

const DisableAssignedMessageModal: React.FC<Props> = ({
  isOpened,
  onClose,
  message,
}: Props) => {
  const orgName =
    useSelector((state: AppState) => organizationSelector(state))?.name || '';
  const [updatePreconfiguredMessage, { isLoading: isConfirming }] =
    useUpdatePreconfiguredMessageMutation();

  const actionName =
    message.type != null && SINGLETON_RESPONSE_TYPES.includes(message.type)
      ? SINGLETON_TYPE_ACTION_LABELS[message.type as SingletonResponseType]
      : '';

  const handleConfirm = useCallback(() => {
    updatePreconfiguredMessage({
      org: orgName,
      id: message.id,
      updates: { enabled: false },
    })
      .unwrap()
      .then(() => notifySuccess(UPDATE_SUCCESS_MESSAGE))
      .catch((error: unknown) => {
        const err = error as { data?: { message?: string } };
        notifyError(err.data?.message || 'Failed to update message');
      })
      .finally(() => onClose());
  }, [orgName, message, onClose, updatePreconfiguredMessage]);

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      width="400px"
      id="modal-disable-assigned-message"
      data-name="disable-assigned-message-modal"
    >
      <ModalBody onClose={onClose} height="100%">
        <p className={styles.title} data-name="disable-assigned-title">
          Disable Assigned Message
        </p>
        <p className={styles.body} data-name="disable-assigned-body">
          This message is currently assigned to the{' '}
          <strong>{actionName}</strong> action. Disabling it will revert the
          action to the default system message.
        </p>
      </ModalBody>
      <ModalFooter>
        <div className={styles.buttonRow}>
          <Button
            btnType="tertiary"
            onClick={onClose}
            grow
            noMargin
            dataName="cancel-disable-assigned-button"
          >
            Cancel
          </Button>
          <Button
            btnType="warning_primary"
            onClick={handleConfirm}
            grow
            noMargin
            isLoadingProp={isConfirming}
            dataName="confirm-disable-assigned-button"
          >
            Disable
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default DisableAssignedMessageModal;
